/**
 * 类型定义文件
 * 定义应用中使用的所有核心类型
 */

/** 格子类型枚举 */
export enum TileType {
  WALK = 0,      // 可走管空间
  FORBID = 1,    // 禁行区
  ROOM = 2,      // 设备机房
  END = 3,       // 末端
  RISER = 4,     // 竖井（立管）
}

/** 网格尺寸 */
export interface GridSize {
  W: number;  // 宽度（X方向格子数）
  D: number;  // 深度（Z方向格子数）
}

/** 机房形状 - 相对坐标数组 */
export type RoomShape = [number, number][];

/** 机房对象 */
export interface Room {
  id: string;          // 机房ID
  x: number;           // 锚点X坐标
  z: number;           // 锚点Z坐标
  color: number;       // 颜色（Three.js颜色值）
  shape: RoomShape;    // 形状（相对格子坐标）
  floorIdx: number;    // 所在楼层索引
  systemId: number;    // 所属管线系统ID
  added?: boolean;     // 是否为用户手动添加
}

/** 末端对象 */
export interface End {
  id: string;          // 末端ID
  x: number;           // X坐标
  z: number;           // Z坐标
  color: number;       // 颜色
  floorIdx: number;    // 所在楼层索引
  systemId: number;    // 所属管线系统ID
  added?: boolean;     // 是否为用户手动添加
}

/** 顶点坐标 */
export interface Vertex {
  x: number;
  z: number;
}

/** 格子坐标 */
export interface Cell {
  x: number;
  z: number;
}

/** 走管线条 */
export interface Line {
  vertices: Vertex[];  // 顶点数组
  cells: Cell[];       // 栅格化后的格子数组
}

/** 交接点 */
export interface Junction {
  x: number;
  z: number;
}

/** 楼层对象 */
export interface Floor {
  id: string;                    // 楼层ID
  name: string;                  // 楼层名称
  yBase: number;                 // 世界Y坐标基准
  type: TileType[][];            // 格子类型矩阵
  rooms: Room[];                 // 机房列表
  ends: End[];                   // 末端列表
  lines: Line[];                 // 走管线条列表
  drawnCells: Set<string>;       // 被走管线覆盖的格子
  drawnOrig: Map<string, TileType>;  // 走管覆盖前的原始类型
  endOrig: Map<string, TileType>;    // 末端覆盖前的原始类型
  roomOrig: Map<string, TileType>;  // 机房覆盖前的原始类型
  riserOrig: Map<string, TileType>; // 竖井覆盖前的原始类型
  junctions: Junction[];          // 走管线交接点
  bindings: Map<string, Set<string>>; // 机房ID -> 末端ID集合的绑定关系
}

/** 3D节点（用于寻路） */
export interface Node3D {
  fi: number;  // 楼层索引
  x: number;   // X坐标
  z: number;   // Z坐标
}

/** 路径结果 */
export interface PathResult {
  path: Node3D[] | null;  // 路径节点序列
  visited: Node3D[];      // 访问过的节点（用于可视化）
}

/** 单个末端的路由结果 */
export interface RouteResult {
  end: End;                // 末端对象
  len: number;             // 路径长度
  cor: number;             // 折点数
  ok: boolean;             // 是否成功连通
  from: string | null;     // 连接的机房ID
  reason?: string;         // 失败原因
  visited?: Node3D[];      // 访问过的节点
}

/** 管线系统（专业分类：给水/排水/暖通/电气等） */
export interface System {
  id: number;          // 系统ID
  name: string;        // 系统名称
  color: number;       // 系统颜色
  abbr: string;        // 简称（水/暖/电...），显示在机房/末端名后
}

/** 场景快照（用于撤销/重做） */
export interface SceneSnapshot {
  layoutIdx: number;
  bindMode: boolean;
  activeFloorIdx: number;
  floorCounter: number;
  roomCounter: number;
  endCounter: number;
  shaftCounter: number;
  systemCounter: number;
  activeSystemId: number;
  cornerPenalty: number;
  curLine: Vertex[];
  floors: Floor[];
  shafts: Shaft[];
  systems: System[];
}

/** 导出的场景数据 */
export interface ExportScene {
  schema: string;
  version: number;
  grid: GridSize;
  layoutIdx: number;
  bindMode: boolean;
  activeFloorIdx: number;
  floorCounter: number;
  roomCounter: number;
  endCounter: number;
  shaftCounter: number;
  systemCounter: number;
  cornerPenalty: number;
  floors: ExportFloor[];
  shafts: Shaft[];
  systems: System[];
}

/** 导出的楼层数据 */
export interface ExportFloor {
  id: string;
  name: string;
  yBase: number;
  type: TileType[][];
  rooms: Room[];
  ends: End[];
  lines: Line[];
  bindings: Record<string, string[]>;
  endOrig: Record<string, TileType>;
  roomOrig: Record<string, TileType>;
  drawnOrig: Record<string, TileType>;
  riserOrig: Record<string, TileType>;
}

/** 竖井对象（跨层立管，注册表中的唯一数据源） */
export interface Shaft {
  id: number;          // 编号（1,2,3...）
  x: number;           // X坐标
  z: number;           // Z坐标
  floorFrom: number;   // 起始楼层索引（含）
  floorTo: number;     // 终止楼层索引（含）
  systemId: number;    // 所属管线系统（只有同系统管线可通过此竖井跨层）
}

/** 竖井柱信息（用于渲染） */
export interface ShaftColumn {
  id: number;
  x: number;
  z: number;
  yMin: number;
  yMax: number;
  floorIds: number[];
  systemId: number;
}

/** 选中对象类型 */
export enum SelectionKind {
  CELL = 'cell',
  ROOM = 'room',
  END = 'end',
  SHAFT = 'shaft',
}

/** 选中对象 */
export interface Selection {
  kind: SelectionKind;
  ref: Room | End | Shaft | null;
  cell: Cell;
}

/** 应用状态 */
export interface AppState {
  floors: Floor[];
  f: Floor | null;
  activeFloorIdx: number;
  floorCounter: number;
  mode: string;
  bindMode: boolean;
  layoutIdx: number;
  planView: boolean;
  roomCounter: number;
  endCounter: number;
  results: RouteResult[];
  running: boolean;
  curLine: Vertex[];
  hoverCell: Cell | null;
  dragEnd: End | null;
  dragRoom: Room | null;
  dragMoved: boolean;
  downCell: Cell | null;
  downOnEnd: End | null;
  downX: number;
  downY: number;
  roomGrabDX: number;
  roomGrabDZ: number;
}

/** 图层定义 */
export interface LayerDef {
  id: string;
  name: string;
  visible: boolean;
  locked: boolean;
  group: string;
  kind: SelectionKind | null;
}
